import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetIndicatorsByParentIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetIndicatorsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcIndicatorsToGraphQL = (
  trpcData: GetIndicatorsByParentIdResponse
): GetIndicatorsByParentIdQuery => {
  return trpcData;
};

export const useGetIndicatorsByParentId = (
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.indicator.indicatorsByParentId.queryOptions({
      parentId,
    }),

    enabled: trpcEnabled && !skip, // Only enable when TRPC flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcIndicatorsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => mapTRPCRefetch(trpcRefetch, mapTrpcIndicatorsToGraphQL),
    error: trpcError,
  };
};
